import {
  Box,
  Image,
  SimpleSearch,
  Text,
  TextLink,
} from '@redbubble/design-system';
import { RedbubbleLayout } from '@redbubble/next/app';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { withRBStaticProps } from '@redbubble/next/props';
import {
  AppProps,
  HeaderAndFooterVariant,
  UserInfo,
} from '@redbubble/next/types';
import { DEFAULT_LOCALE } from '@redbubble/next-i18n';
import { NextSeo } from 'next-seo';
import { DOCUMENT_TITLE_404 } from '../constants';
import { getImageUrl } from '../utils/common';
import styles from './404.module.scss';

export const getStaticProps = withRBStaticProps();

const messages = defineMessages({
  headerMessage: {
    defaultMessage: 'Houston, we have a problem.',
  },
  body: {
    defaultMessage:
      "Don't worry though, there are countless unique designs to discover.",
  },
  searchPlaceholder: {
    defaultMessage: 'Find the very thing',
  },
  designedAndSoldBy: {
    defaultMessage: '{workTitle} designed and sold by {artistName}',
  },
});

const Custom404 = ({
  userInfo,
}: Partial<Omit<AppProps, 'userInfo'> & { userInfo: Partial<UserInfo> }>) => {
  const intl = useIntl();
  const localeStr =
    userInfo && userInfo.locale && userInfo.locale !== DEFAULT_LOCALE
      ? `/${userInfo.locale}`
      : '';

  return (
    <>
      <NextSeo noindex={true} nofollow={false} title={DOCUMENT_TITLE_404} />
      <Box className={styles.container}>
        <Box className={styles.textContent}>
          <Text element="h2" className={styles.headerMessage}>
            404. {intl.formatMessage(messages.headerMessage)}
          </Text>
          <Text type="display4">{intl.formatMessage(messages.body)}</Text>
          <SimpleSearch intl={intl} baseUrl={localeStr} />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{ position: 'relative' }}
        >
          <Box className={styles.imgContainer}>
            <Image
              src={getImageUrl('/error/artwork.jpg')}
              alt={intl.formatMessage(messages.designedAndSoldBy, {
                workTitle: 'Fallen Moon',
                artistName: 'kelvinstagram',
              })}
              className={styles.artwork}
            />
            <Image
              alt=""
              src={getImageUrl('/error/star1.png')}
              className={styles.starRound}
            />
            <Image
              alt=""
              src={getImageUrl('/error/star2.png')}
              className={styles.starBig}
            />
            <Image
              alt=""
              src={getImageUrl('/error/star3.png')}
              className={styles.starSmall}
            />
          </Box>
          <Box className={styles.imageCaption}>
            <TextLink
              href="https://www.redbubble.com/people/kelvinstagram/shop"
              target="_blank"
              type="caption"
            >
              @kelvinstagram
            </TextLink>
          </Box>
        </Box>
      </Box>
    </>
  );
};

Custom404.layout = (props) => {
  return RedbubbleLayout({
    ...props,
    headerAndFooterVariant: HeaderAndFooterVariant.Static,
  });
};

export default Custom404;
